import React, { useState } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { toast } from '../hooks/use-toast';
import { mockISOSignup, mockBusinessApply } from '../mock';
import { Phone, Mail, TrendingUp, Shield, Zap, Clock, ArrowRight, Building2 } from 'lucide-react';
import './Home.css';

const Home = () => {
  const [isoDialogOpen, setIsoDialogOpen] = useState(false);
  const [businessDialogOpen, setBusinessDialogOpen] = useState(false);
  
  const [isoForm, setIsoForm] = useState({
    businessName: '',
    fullName: '',
    email: '',
    phone: ''
  });
  
  const [businessForm, setBusinessForm] = useState({
    businessName: '',
    fullName: '',
    email: '',
    phone: '',
    amount: '',
    businessAddress: '',
    city: '',
    state: '',
    zipCode: '',
    annualRevenue: '',
    timeInBusiness: '',
    creditScore: '',
    businessType: '',
    purposeOfFunds: ''
  });

  const handleISOSubmit = async (e) => {
    e.preventDefault();
    const result = await mockISOSignup(isoForm);
    toast({ title: result.message });
    setIsoDialogOpen(false);
    setIsoForm({ businessName: '', fullName: '', email: '', phone: '' });
  };

  const handleBusinessSubmit = async (e) => {
    e.preventDefault();
    const result = await mockBusinessApply(businessForm);
    toast({ title: result.message });
    setBusinessDialogOpen(false);
    setBusinessForm({ 
      businessName: '', fullName: '', email: '', phone: '', amount: '',
      businessAddress: '', city: '', state: '', zipCode: '',
      annualRevenue: '', timeInBusiness: '', creditScore: '', businessType: '', purposeOfFunds: ''
    });
  };

  return (
    <div className="wallstreet-container">
      {/* Header */}
      <header className="wallstreet-header">
        <div className="header-wrapper">
          <div className="logo-container">
            <img 
              src="https://customer-assets.emergentagent.com/job_b0852d6c-d2df-4e7d-86bc-e76cd1461f1d/artifacts/16hs5hyw_f93574eb-dedc-4418-b04a-98d51875dbae.png" 
              alt="Norwood Advance LLC" 
              className="header-logo"
            />
          </div>
          <nav className="header-nav">
            <a href="#solutions">Solutions</a>
            <a href="#about">About</a>
            <a href="#contact">Contact</a>
            <div className="header-phone">
              <Phone size={18} />
              <a href="tel:7186140573">(718) 614-0573</a>
            </div>
            <Dialog open={isoDialogOpen} onOpenChange={setIsoDialogOpen}>
              <DialogTrigger asChild>
                <Button variant="outline" className="iso-btn">ISO Signup</Button>
              </DialogTrigger>
              <DialogContent className="iso-dialog">
                <DialogHeader>
                  <DialogTitle>Become an ISO Partner</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleISOSubmit} className="iso-form">
                  <div className="form-field">
                    <Label htmlFor="iso-businessName">Business Name *</Label>
                    <Input
                      id="iso-businessName"
                      value={isoForm.businessName}
                      onChange={(e) => setIsoForm({...isoForm, businessName: e.target.value})}
                      required
                    />
                  </div>
                  <div className="form-field">
                    <Label htmlFor="iso-fullName">Full Name *</Label>
                    <Input
                      id="iso-fullName"
                      value={isoForm.fullName}
                      onChange={(e) => setIsoForm({...isoForm, fullName: e.target.value})}
                      required
                    />
                  </div>
                  <div className="form-field">
                    <Label htmlFor="iso-email">Email *</Label>
                    <Input
                      id="iso-email"
                      type="email"
                      value={isoForm.email}
                      onChange={(e) => setIsoForm({...isoForm, email: e.target.value})}
                      required
                    />
                  </div>
                  <div className="form-field">
                    <Label htmlFor="iso-phone">Phone Number *</Label>
                    <Input
                      id="iso-phone"
                      type="tel"
                      value={isoForm.phone}
                      onChange={(e) => setIsoForm({...isoForm, phone: e.target.value})}
                      required
                    />
                  </div>
                  <Button type="submit" className="submit-btn">Submit Application</Button>
                </form>
              </DialogContent>
            </Dialog>
            <Dialog open={businessDialogOpen} onOpenChange={setBusinessDialogOpen}>
              <DialogTrigger asChild>
                <Button className="apply-btn">Apply Now</Button>
              </DialogTrigger>
            </Dialog>
          </nav>
        </div>
      </header>

      {/* Hero Section */}
      <section className="hero-wallstreet">
        <div className="hero-image-bg">
          <img src="https://customer-assets.emergentagent.com/job_advance-financing/artifacts/gijx1u2d_abel-perez-jj8ldRbRpPw-unsplash.jpg" alt="Corporate skyline" />
          <div className="hero-overlay"></div>
        </div>
        <div className="hero-content-wrapper">
          <h1 className="hero-heading">
            Capital That
            <span className="hero-accent">Moves At</span>
            The Speed Of Business
          </h1>
          <p className="hero-description">
            Enterprise-grade financing solutions. Transparent terms. Strategic growth capital for businesses that refuse to wait.
          </p>
          <Dialog open={businessDialogOpen} onOpenChange={setBusinessDialogOpen}>
            <DialogTrigger asChild>
              <Button size="lg" className="hero-cta">Get Funded <ArrowRight className="ml-2" /></Button>
            </DialogTrigger>
            <DialogContent className="business-dialog mca-application">
              <DialogHeader>
                <DialogTitle>Merchant Cash Advance Application</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleBusinessSubmit} className="business-form">
                <h3 className="form-section-title">Business Information</h3>
                <div className="form-row">
                  <div className="form-field">
                    <Label htmlFor="bus-businessName">Business Name *</Label>
                    <Input
                      id="bus-businessName"
                      value={businessForm.businessName}
                      onChange={(e) => setBusinessForm({...businessForm, businessName: e.target.value})}
                      required
                    />
                  </div>
                  <div className="form-field">
                    <Label htmlFor="bus-businessType">Business Type *</Label>
                    <Input
                      id="bus-businessType"
                      placeholder="e.g., LLC, Corporation"
                      value={businessForm.businessType}
                      onChange={(e) => setBusinessForm({...businessForm, businessType: e.target.value})}
                      required
                    />
                  </div>
                </div>
                
                <div className="form-field">
                  <Label htmlFor="bus-businessAddress">Business Address *</Label>
                  <Input
                    id="bus-businessAddress"
                    value={businessForm.businessAddress}
                    onChange={(e) => setBusinessForm({...businessForm, businessAddress: e.target.value})}
                    required
                  />
                </div>
                
                <div className="form-row">
                  <div className="form-field">
                    <Label htmlFor="bus-city">City *</Label>
                    <Input
                      id="bus-city"
                      value={businessForm.city}
                      onChange={(e) => setBusinessForm({...businessForm, city: e.target.value})}
                      required
                    />
                  </div>
                  <div className="form-field">
                    <Label htmlFor="bus-state">State *</Label>
                    <Input
                      id="bus-state"
                      placeholder="NY"
                      value={businessForm.state}
                      onChange={(e) => setBusinessForm({...businessForm, state: e.target.value})}
                      required
                    />
                  </div>
                  <div className="form-field">
                    <Label htmlFor="bus-zipCode">Zip Code *</Label>
                    <Input
                      id="bus-zipCode"
                      value={businessForm.zipCode}
                      onChange={(e) => setBusinessForm({...businessForm, zipCode: e.target.value})}
                      required
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-field">
                    <Label htmlFor="bus-timeInBusiness">Time in Business *</Label>
                    <Input
                      id="bus-timeInBusiness"
                      placeholder="e.g., 5 years"
                      value={businessForm.timeInBusiness}
                      onChange={(e) => setBusinessForm({...businessForm, timeInBusiness: e.target.value})}
                      required
                    />
                  </div>
                  <div className="form-field">
                    <Label htmlFor="bus-annualRevenue">Annual Revenue *</Label>
                    <Input
                      id="bus-annualRevenue"
                      placeholder="$1,000,000"
                      value={businessForm.annualRevenue}
                      onChange={(e) => setBusinessForm({...businessForm, annualRevenue: e.target.value})}
                      required
                    />
                  </div>
                </div>

                <h3 className="form-section-title">Owner Information</h3>
                <div className="form-row">
                  <div className="form-field">
                    <Label htmlFor="bus-fullName">Full Name *</Label>
                    <Input
                      id="bus-fullName"
                      value={businessForm.fullName}
                      onChange={(e) => setBusinessForm({...businessForm, fullName: e.target.value})}
                      required
                    />
                  </div>
                  <div className="form-field">
                    <Label htmlFor="bus-creditScore">Credit Score *</Label>
                    <Input
                      id="bus-creditScore"
                      placeholder="e.g., 720"
                      value={businessForm.creditScore}
                      onChange={(e) => setBusinessForm({...businessForm, creditScore: e.target.value})}
                      required
                    />
                  </div>
                </div>
                
                <div className="form-row">
                  <div className="form-field">
                    <Label htmlFor="bus-email">Email *</Label>
                    <Input
                      id="bus-email"
                      type="email"
                      value={businessForm.email}
                      onChange={(e) => setBusinessForm({...businessForm, email: e.target.value})}
                      required
                    />
                  </div>
                  <div className="form-field">
                    <Label htmlFor="bus-phone">Phone Number *</Label>
                    <Input
                      id="bus-phone"
                      type="tel"
                      value={businessForm.phone}
                      onChange={(e) => setBusinessForm({...businessForm, phone: e.target.value})}
                      required
                    />
                  </div>
                </div>

                <h3 className="form-section-title">Financing Details</h3>
                <div className="form-row">
                  <div className="form-field">
                    <Label htmlFor="bus-amount">Requested Amount *</Label>
                    <Input
                      id="bus-amount"
                      placeholder="$500,000"
                      value={businessForm.amount}
                      onChange={(e) => setBusinessForm({...businessForm, amount: e.target.value})}
                      required
                    />
                  </div>
                  <div className="form-field">
                    <Label htmlFor="bus-purposeOfFunds">Purpose of Funds *</Label>
                    <Input
                      id="bus-purposeOfFunds"
                      placeholder="e.g., Working Capital, Expansion"
                      value={businessForm.purposeOfFunds}
                      onChange={(e) => setBusinessForm({...businessForm, purposeOfFunds: e.target.value})}
                      required
                    />
                  </div>
                </div>

                <Button type="submit" className="submit-btn">Submit MCA Application</Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </section>

      {/* Stats Bar */}
      <section className="stats-bar">
        <div className="stat-item">
          <h3>$7B+</h3>
          <p>Capital Deployed</p>
        </div>
        <div className="stat-divider"></div>
        <div className="stat-item">
          <h3>65,000+</h3>
          <p>Businesses Funded</p>
        </div>
        <div className="stat-divider"></div>
        <div className="stat-item">
          <h3>4 Hours</h3>
          <p>Average Decision Time</p>
        </div>
        <div className="stat-divider"></div>
        <div className="stat-item">
          <h3>$5M</h3>
          <p>Maximum Financing</p>
        </div>
      </section>

      {/* Strategy Section */}
      <section className="strategy-section" id="about">
        <div className="strategy-content">
          <div className="strategy-text">
            <h2>Strategic Capital
For Strategic Minds</h2>
            <p>
              In corporate finance, timing isn't everything—it's the only thing. While traditional lenders trap you in bureaucratic delays, Norwood Advance delivers institutional-grade capital at entrepreneurial speed.
            </p>
            <p>
              Our quantitative approach combines AI-driven risk assessment with human expertise, ensuring you get the capital structure your business deserves, not just what legacy systems allow.
            </p>
            <Button variant="outline" className="strategy-btn">Explore Solutions</Button>
          </div>
          <div className="strategy-image">
            <img src="https://customer-assets.emergentagent.com/job_advance-financing/artifacts/f1km3i69_jeshoots-com-fzOITuS1DIQ-unsplash.jpg" alt="Strategic planning" />
          </div>
        </div>
      </section>

      {/* Solutions Grid */}
      <section className="solutions-section" id="solutions">
        <h2 className="section-title">Precision-Engineered
Financing Solutions</h2>
        <div className="solutions-grid">
          <Card className="solution-card">
            <CardContent className="solution-content">
              <Zap className="solution-icon" size={40} />
              <h3>Working Capital</h3>
              <p>Liquidity when you need it. Flexible terms that adapt to your cash flow cycles. No restrictive covenants.</p>
            </CardContent>
          </Card>

          <Card className="solution-card">
            <CardContent className="solution-content">
              <TrendingUp className="solution-icon" size={40} />
              <h3>Growth Capital</h3>
              <p>Fuel expansion without dilution. Strategic financing for market opportunities that won't wait.</p>
            </CardContent>
          </Card>

          <Card className="solution-card">
            <CardContent className="solution-content">
              <Building2 className="solution-icon" size={40} />
              <h3>Equipment Financing</h3>
              <p>Deploy assets without depleting reserves. Preserve working capital while scaling operations.</p>
            </CardContent>
          </Card>

          <Card className="solution-card">
            <CardContent className="solution-content">
              <Shield className="solution-icon" size={40} />
              <h3>Bridge Financing</h3>
              <p>Strategic capital for critical transitions. M&A, restructuring, or market pivots—we move fast.</p>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Executive Section */}
      <section className="executive-section">
        <div className="executive-bg">
          <img src="https://customer-assets.emergentagent.com/job_advance-financing/artifacts/b44u0h5d_austin-distel-jpHw8ndwJ_Q-unsplash.jpg" alt="Executive meeting" />
          <div className="executive-overlay"></div>
        </div>
        <div className="executive-content">
          <h2>Built For CFOs.
Trusted By CEOs.</h2>
          <p>
            Norwood Advance partners with enterprises that understand financial leverage isn't just about capital—it's about strategic timing, competitive positioning, and operational flexibility.
          </p>
          <Button className="executive-btn">Schedule Consultation</Button>
        </div>
      </section>

      {/* Process Section */}
      <section className="process-section">
        <h2 className="section-title">From Application
To Deployment</h2>
        <div className="process-grid">
          <div className="process-item">
            <div className="process-number">01</div>
            <h3>Intelligent Assessment</h3>
            <p>AI-powered analysis of your business fundamentals, market position, and growth trajectory.</p>
          </div>
          <div className="process-item">
            <div className="process-number">02</div>
            <h3>Structured Offers</h3>
            <p>Multiple financing structures tailored to your capital needs and strategic objectives.</p>
          </div>
          <div className="process-item">
            <div className="process-number">03</div>
            <h3>Rapid Deployment</h3>
            <p>Capital deployed within 24 hours of acceptance. Because opportunities don't wait.</p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="final-cta">
        <div className="cta-bg">
          <img src="https://customer-assets.emergentagent.com/job_advance-financing/artifacts/nnqxe7et_andreas-brucker-C2Dyr5FhGPQ-unsplash.jpg" alt="Corporate architecture" />
          <div className="cta-overlay"></div>
        </div>
        <div className="cta-content-final">
          <h2>Stop Waiting.
Start Growing.</h2>
          <p>Join 65,000+ businesses that chose velocity over bureaucracy.</p>
          <Button size="lg" className="cta-final-btn" onClick={() => setBusinessDialogOpen(true)}>
            Apply for Financing
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="main-footer" id="contact">
        <div className="footer-wrapper">
          <div className="footer-main">
            <div className="footer-brand">
              <img 
                src="https://customer-assets.emergentagent.com/job_b0852d6c-d2df-4e7d-86bc-e76cd1461f1d/artifacts/16hs5hyw_f93574eb-dedc-4418-b04a-98d51875dbae.png" 
                alt="Norwood Advance LLC" 
                className="footer-logo"
              />
              <p className="footer-tagline">Enterprise financing for the modern economy.</p>
            </div>
            <div className="footer-links">
              <div className="footer-col">
                <h4>Solutions</h4>
                <a href="#working">Working Capital</a>
                <a href="#growth">Growth Capital</a>
                <a href="#equipment">Equipment Financing</a>
                <a href="#bridge">Bridge Financing</a>
              </div>
              <div className="footer-col">
                <h4>Company</h4>
                <a href="#about">About</a>
                <a href="#careers">Careers</a>
                <a href="#partners">Partners</a>
                <a href="#iso" onClick={() => setIsoDialogOpen(true)}>Become an ISO</a>
              </div>
              <div className="footer-col">
                <h4>Contact</h4>
                <p><Phone size={16} /> <a href="tel:7186140573">(718) 614-0573</a></p>
                <p><Mail size={16} /> <a href="mailto:Mayer@norwoodadvance.com">Mayer@norwoodadvance.com</a></p>
                <p className="contact-name">Mayer Goodman</p>
              </div>
            </div>
          </div>
          <div className="footer-bottom">
            <p>© 2026 Norwood Advance LLC. All rights reserved.</p>
            <div className="footer-legal">
              <a href="#privacy">Privacy</a>
              <a href="#terms">Terms</a>
              <a href="#compliance">Compliance</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Home;